import { useState } from 'react';
import { ArrowLineDown, ArrowLineUp, Clock, X } from '@phosphor-icons/react';
import { formatCurrency } from '../../../../../../utils/CurrencyUtils';
import { formatDate } from '../../../../../../utils/DateUtils';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { PendingTransactionItem } from '../../../../../../types/apiTypes';
import {
  useDepositCustomerId,
  usePendingTransactionsQuery,
} from '../hooks/useDeposits';

/**
 * The "Processing" band above the transactions feed: in-flight transactions that haven't settled into
 * real Transactions yet — deposits (ACH debit pulls) and withdrawals, in one mixed list. Direction is
 * driven by the sign of `amount` (positive = money in, negative = money out): the row icon, the
 * fallback label, and the amount formatting all follow it. It renders NOTHING until there's at least
 * one pending row, so it's inert until a customer has one in flight. Refreshes automatically — the
 * deposit mutation and the withdraw modal both invalidate the `['pendingTransactions', customerId]`
 * cache key on a successful submit.
 *
 * `enabled` lets the feed suppress the fetch for pre-active / demo customers (who have no real data).
 */
export const PendingTransactionsBand = ({
  enabled = true,
}: {
  enabled?: boolean;
}) => {
  const { screenView } = useScreenView();
  const isMobile = screenView === 'mobile';
  const customerId = useDepositCustomerId();
  const { data: pending = [] } = usePendingTransactionsQuery(customerId, enabled);
  // Ephemeral dismissal — local state only (NOT persisted). Closing it just clears the space for this
  // visit; it reappears next time the feed mounts (navigate away and back), by design.
  const [dismissed, setDismissed] = useState(false);

  // Silent until there's something in flight — no banner (or spinner) flash on every feed load.
  if (pending.length === 0 || dismissed) return null;

  const countLabel =
    pending.length === 1 ? '1 transfer' : `${pending.length} transfers`;

  return (
    // `shrink-0`: the transactions feed is a fixed-height `flex flex-col` container, so without this
    // the band gets compressed by the tall table below it (and its `overflow-hidden` clips it away).
    <div className="w-full shrink-0 overflow-hidden rounded-[4px] border border-infoPrimary/25 bg-[#EAF4FE] font-inter shadow-sm">
      <div className="relative flex items-start gap-3 px-4 py-3.5 md:px-5">
        {/* Left icon box on tablet/desktop only. On phones (the app's <600px 'mobile' view) it's
            dropped and a smaller clock sits inline by "Pending" so the rows use (almost) full width. */}
        {!isMobile && (
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[4px] bg-infoPrimary/10">
            <Clock
              size={18}
              weight="fill"
              className="text-infoPrimary"
              aria-label="Processing"
            />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {isMobile && (
              <Clock
                size={15}
                weight="fill"
                className="text-infoPrimary"
                aria-label="Processing"
              />
            )}
            <h3 className="text-sm font-semibold leading-snug text-primary md:text-[15px]">
              Pending
            </h3>
            <span className="inline-flex flex-shrink-0 items-center rounded-[4px] bg-infoPrimary/10 px-1.5 py-0.5 text-[11px] font-semibold text-infoPrimary">
              {countLabel}
            </span>
          </div>

          {!isMobile && (
            <p className="mt-1 text-xs leading-relaxed text-graysecondary">
              Transfers typically take 1-3 business days to complete.
            </p>
          )}

          <ul className="mt-2.5 flex flex-col gap-1.5">
            {pending.map((p: PendingTransactionItem) => {
              // Direction from the sign of `amount` (positive = money in / deposit, negative = money
              // out / withdrawal): drives the arrow and the fallback label. `formatCurrency` already
              // renders negatives as `-$…`, so the amount below needs no per-direction handling.
              const isOutbound = p.amount < 0;
              const DirectionIcon = isOutbound ? ArrowLineUp : ArrowLineDown;
              const fallbackLabel = isOutbound
                ? 'Withdrawal from TACH'
                : 'Deposit into TACH';
              return (
                <li
                  key={p.pending_transaction_id}
                  className="flex items-center justify-between gap-3 rounded-[4px] border border-black/5 bg-white/70 px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <DirectionIcon
                      size={16}
                      className="shrink-0 text-secondary"
                    />
                    <span
                      className={`text-sm text-primary ${
                        isMobile ? 'line-clamp-2' : 'truncate'
                      }`}
                    >
                      {p.description || fallbackLabel}
                    </span>
                  </div>
                  <div
                    className={`flex shrink-0 ${
                      isMobile
                        ? 'flex-col items-end gap-0.5'
                        : 'items-center gap-3'
                    }`}
                  >
                    <span className="text-sm font-semibold text-primary">
                      {formatCurrency(p.amount)}
                    </span>
                    {/* On mobile the date stacks under the amount (xs, grayed); on larger screens it
                        sits inline to its left. */}
                    <span className="whitespace-nowrap text-xs text-graysecondary">
                      {formatDate(new Date(p.created_at), false, true)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="absolute right-2 top-2 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[4px] text-graysecondary transition-colors hover:bg-black/5 hover:text-primary"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default PendingTransactionsBand;
